import { useContext, useEffect, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { asadoSdk } from '@fff-web/asado-sdk';
import type { LegacyLinks } from '@fff-web/asado-sdk';
import type { Env } from '@fff-web/constants';
import type { UserProfileData } from '@fff-web/sdk-types';
import GlobalNavInternalContext from '../context/GlobalNavInternalContext';

export const useNavbarRoutes = () => {
  const { env } = useContext(GlobalNavInternalContext);

  const asadoSdkInstance = useMemo(
    () =>
      asadoSdk(env.REACT_APP_ENVIRONMENT as Env, true, undefined, {
        platform: 'web-legacy',
        version: `${process.env.APP_NAME ?? 'unknown'}@${process.env.APP_VERSION ?? 'unknown'}`,
      }),
    [env.REACT_APP_ENVIRONMENT],
  );

  const [legacyLinks, setLegacyLinks] = useState<LegacyLinks | null>(null);
  const [userProfileData, setUserProfileData] = useState<UserProfileData | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let isCancelled = false;

    const fetchNavbarRoutes = async () => {
      try {
        const response = await asadoSdkInstance.getNavbarProfile(undefined, { legacy: true });

        if (isCancelled || abortController.signal.aborted) return;

        if (!response.ok) return;

        const payload = response.data.data;

        if (!payload) return;

        setLegacyLinks(payload.legacyLinks);
        setUserProfileData(payload.userProfileData);
      } catch (error) {
        if (!isCancelled && !abortController.signal.aborted) {
          datadogLogs.logger.error('Unable to fetch navbar routes', {}, error as Error);
        }
      }
    };

    fetchNavbarRoutes();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [asadoSdkInstance]);

  return { legacyLinks, userProfileData };
};
